import * as Popover from '@radix-ui/react-popover';
import { Link } from 'react-router-dom';
import { Spinner } from './Spinner';
import { useMetricDefinitions } from '@/hooks/use-metric-definition';
import { classify, type IMetricDefinition, type MetricTierColor } from '@skystone/shared';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Colour mapping (module-scoped — S7721)
// ---------------------------------------------------------------------------

const DOT_CLASS: Record<MetricTierColor, string> = {
  success: 'bg-success',
  primary: 'bg-primary',
  warning: 'bg-warning',
  error: 'bg-error',
};

const HIGHLIGHT_CLASS = 'bg-muted/60 rounded';

// Throughput fallback text
const THROUGHPUT_NOTE =
  "Throughput targets are team-specific — they depend on team size, mix of work and product complexity. The most important benchmark is your own team's historical average.";

// ---------------------------------------------------------------------------
// Popover content (exported for reuse)
// ---------------------------------------------------------------------------

interface PopoverContentProps {
  definition: IMetricDefinition;
  currentValue?: number | null;
}

export function MetricInfoPopoverContent({ definition, currentValue }: Readonly<PopoverContentProps>) {
  const matchingTierId =
    currentValue != null && definition.tiers.length > 0
      ? classify(currentValue, { metricKey: definition.metricKey, direction: definition.direction, tiers: definition.tiers })
      : null;

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="text-lg" aria-hidden="true">{definition.icon}</span>
        <div>
          <p className="text-sm font-semibold text-foreground">{definition.name}</p>
          <p className="text-xs italic text-muted-foreground">{definition.tagline}</p>
        </div>
      </div>

      {/* Definition (full — the last sentence carries the key nuance) */}
      <p className="text-xs leading-relaxed text-muted-foreground">
        {definition.definition}
      </p>

      {/* Tier list or throughput note */}
      {definition.tiers.length > 0 ? (
        <ul className="list-none space-y-1" aria-label="Tier thresholds">
          {[...definition.tiers]
            .sort((a, b) => a.tierOrder - b.tierOrder)
            .map((tier) => (
              <li
                key={tier.tierId}
                className={cn(
                  'flex items-center gap-2 px-1 py-0.5',
                  tier.tierId === matchingTierId && HIGHLIGHT_CLASS,
                )}
                data-testid={`popover-tier-${tier.tierId}`}
              >
                <span
                  className={cn('inline-block h-2 w-2 flex-shrink-0 rounded-full', DOT_CLASS[tier.colorToken])}
                  aria-hidden="true"
                />
                <span className="text-[10px] font-semibold uppercase text-foreground w-14">{tier.label}</span>
                <span className="text-[10px] text-muted-foreground">{tier.description}</span>
              </li>
            ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">{THROUGHPUT_NOTE}</p>
      )}

      {/* Learn more link */}
      <Link
        to={`/metric-catalogue#${definition.metricKey}`}
        className="inline-block text-xs font-medium text-primary hover:underline"
      >
        Learn more →
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MetricInfoPopover — Radix-backed trigger + portalled panel
//
// The panel is rendered into a portal (document.body) so no ancestor CSS
// (uppercase, overflow:hidden, transform, stacking contexts, …) can affect
// the popover's appearance or placement. Radix handles trigger toggling,
// click-outside, Escape, focus management, and viewport-aware positioning.
// ---------------------------------------------------------------------------

interface MetricInfoPopoverProps {
  metricKey: string;
  currentValue?: number | null;
  className?: string;
}

export function MetricInfoPopover({ metricKey, currentValue, className }: Readonly<MetricInfoPopoverProps>) {
  const { definitions, isLoading } = useMetricDefinitions();
  const definition = definitions.find((d) => d.metricKey === metricKey);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Info about ${definition?.name ?? metricKey}`}
          className={cn(
            'ml-1 inline-flex items-center text-muted-foreground hover:text-foreground focus:outline-none',
            className,
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-3.5 w-3.5"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 w-80 rounded-lg border border-border bg-card p-4 normal-case text-left shadow-lg animate-in fade-in-0 slide-in-from-top-1"
          aria-label={`${definition?.name ?? metricKey} information`}
        >
          {isLoading || !definition ? (
            <div className="flex items-center justify-center py-4"><Spinner /></div>
          ) : (
            <MetricInfoPopoverContent definition={definition} currentValue={currentValue} />
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
